'use strict';

import { normalizeEvent } from '@solink/vd-data-layer/api-clients/shared/eventUtils';
import settings from '@solinkcorp/settings';
import { toast } from '@solinkcorp/solink-components';
import {
  EVENT_SUBTYPE_ALARM,
  EVENT_TYPE_BOOKMARK,
  EVENT_TYPE_MOTION,
  EVENT_TYPE_TRANSACTION,
} from '@solinkcorp/vd-common';
import { CallhomeApi, PlatformApi } from 'clients';
import { AppDispatcher } from 'dispatcher';
import AnalyticsLogger from 'models/AnalyticsLogger';
import AuthService from 'models/AuthService';
import { AuthStore } from 'stores/AuthStore';
import EventFilterStore from 'stores/EventFilterStore';
import * as DateHandler from 'utils/DateHandler';
import {
  generateQueryParameters,
  getQueryParameters,
  parseSearchCapsules,
} from 'utils/EventsFetchUtil';
import { doesEventTabHaveEventsHiddenFromDisplay } from 'utils/FeatureStandardUtils';
import ReportUtil from 'utils/ReportUtil';
import {
  CLEAR_ACTIVE_EVENT,
  CLOSE_EVENT_PLAYER,
  CLOUD_POLL_START,
  CLOUD_POLL_STOP,
  CONNECT_POLL_START,
  CONNECT_POLL_STOP,
  CREATE_EVENT,
  CREATE_REPORT_ERROR,
  CREATE_REPORT_SUCCESS,
  DISPATCH_TIMEOUT_MS,
  EXPORT_QUERY_COUNT,
  FAST_NOTIFICATION_DURATION,
  HIDE_FROM_DISPLAY_TAG,
  INHERIT_TYPE_ALL,
  UPDATE_SEARCH_CAPSULES,
  UPDATE_SEARCH_STRING,
} from 'vd-constants';

function _processEvents(events) {
  if (!events) {
    return [];
  }

  const processedEvents = [];

  events.map((item) => {
    item.startTime = new Date(item.startTime);
    item.endTime = new Date(item.endTime);

    if (!item.cameras) {
      item.cameras = [];
    }

    processedEvents.push(item);
  });

  return processedEvents;
}

export default {
  async fetchAllEventsForExport(
    eventStandard,
    featureStandard,
    fieldStats,
    locale,
    progressHandles,
    start,
    end,
    offset,
    events,
    locationIds,
    activeEventFeature,
    cb,
  ) {
    const {
      eventFilterState,
      eventSubtypeFilter = {},
      searchString,
      capsules,
      searchOperator,
    } = EventFilterStore.getAppState();

    const selectedReportId = EventFilterStore.getSelectedReportId();
    const { tabs } = featureStandard;
    const doesTabHaveHiddenEvents = doesEventTabHaveEventsHiddenFromDisplay(
      tabs,
      activeEventFeature,
    );

    const mustIncludeAlarm =
      eventSubtypeFilter && Object.keys(eventSubtypeFilter).includes(EVENT_SUBTYPE_ALARM);

    const eqMustNotFields = mustIncludeAlarm ? undefined : { subtype: EVENT_SUBTYPE_ALARM };

    const params = generateQueryParameters({
      fieldStats,
      start: start.toISOString(),
      end: end.toISOString(),
      locationIds,
      count: EXPORT_QUERY_COUNT,
      offset,
      eventFilterState,
      searchString,
      filters: parseSearchCapsules(eventStandard, fieldStats, capsules),
      selectedReportId,
      eventSubtypeFilter,
      include: doesTabHaveHiddenEvents ? HIDE_FROM_DISPLAY_TAG : null,
      eqMustNotFields,
      source: '#fetchAllEventsForExport',
      searchOperator,
    });

    let next = null;
    let total = 0;
    let count = 0;
    const startTime = Date.now();

    do {
      try {
        params.next = next;
        params.totalLimit = !!next; // On first query request, get the accurate total result count.

        const response = await PlatformApi().listEvents(params);

        if (progressHandles.isExporting() && response && response.results && response.count) {
          count += response.count;
          if (!next) total = response.total;
          const processedData = _processEvents(response.results);

          const now = Date.now();
          const avgTime = (now - startTime) / Math.ceil(count / EXPORT_QUERY_COUNT);
          const endTime =
            now +
            Math.ceil(
              avgTime *
                (Math.ceil(total / EXPORT_QUERY_COUNT) - Math.ceil(count / EXPORT_QUERY_COUNT)),
            );
          const timeRemaining = DateHandler.getDuration(locale, now, endTime);

          const percent = Math.ceil((count / total) * 100);
          const progressText = `${progressHandles.label()}: ${timeRemaining} (${new Date(
            endTime,
          ).toLocaleTimeString()})`;
          progressHandles.update(percent, progressText);

          // append to list
          events = events.concat(processedData);
          next = response.next;
        } else {
          next = null;
        }
      } catch (err) {
        if (err.statusCode && err.statusCode === 401) {
          // AuthActions.logout();
        } else if (err.statusCode && err.statusCode === 400 && events.length >= 10000) {
          cb(events);
        } else {
          console.error('Error fetching events', err);
        }
        return;
      }
    } while (next);

    cb(events);
  },

  /**
   * @param {S3} s3
   */
  async createEvent(eventData, callback, uploadThumbnail = true, s3) {
    const event = eventData[EVENT_TYPE_BOOKMARK] || {};
    const { switchToEvent } = eventData;
    const images = [];

    const user = AuthStore.getUser();
    const tenantId = user.app_metadata.tenantId;
    const startTime = event.startTime || event.startTimeLocal;

    if (uploadThumbnail) {
      event.cameras?.forEach((camera) => {
        const imagePath = `${event.locationId}/${camera.id}/${startTime}.jpg`;

        if (/,/.test(camera.thumbnail)) {
          images.push({
            blob: this._dataUriToBlob(camera.thumbnail),
            path: imagePath,
          });

          camera.thumbnail = imagePath;
        }
      });

      await Promise.all(
        images.map((image) => {
          return s3
            .putObject({
              Bucket: settings.s3_bucket_name,
              Key: `${tenantId}/${image.path}`,
              Body: image.blob,
              ContentType: 'image/jpeg',
            })
            .catch((error) => {
              console.error(`Error uploading image ${image.path}:`, error);
              return null;
            });
        }),
      );
    }

    return await this._createEvent(event, switchToEvent, callback);
  },

  /**
   * Edit an existing event
   * @param {*} eventId
   * @param {*} payload
   * @returns {Promise<any>}
   */
  editEvent(eventId, payload) {
    return new Promise((resolve, reject) => {
      PlatformApi()
        .editEvent(eventId, payload)
        .then((res) => {
          normalizeEvent(res);
          resolve(res);
        })
        .catch((error) => {
          console.error('error while creating new event', error);
          reject(error);
        });
    });
  },

  updateSearchString(value) {
    AppDispatcher.dispatch(UPDATE_SEARCH_STRING, {
      searchString: value,
    });
  },

  updateSearchCapsules(value) {
    AppDispatcher.dispatch(UPDATE_SEARCH_CAPSULES, {
      capsules: value,
    });
  },

  clearActiveEvent() {
    AppDispatcher.dispatch(CLEAR_ACTIVE_EVENT, {});
  },

  closeEventPlayer() {
    AppDispatcher.dispatch(CLOSE_EVENT_PLAYER, {});
  },

  createReport({
    eventStandard,
    fieldStats,
    name,
    locationIds,
    locationTags,
    description = null,
    reportTemplateId,
    query = {},
    transaction = false,
    motion = false,
    bookmark = false,
    inheritType,
    hasDaily,
    hasWeekly,
    hasEmail,
    hasSms,
    hasPush,
    smsNumber,
    feature,
    mergeWithSubtypes,
    locale,
  }) {
    AnalyticsLogger.createReport({ reportName: name });

    const { eventFilterState, searchString, capsules, searchOperator } =
      EventFilterStore.getAppState();

    const filters = parseSearchCapsules(eventStandard, fieldStats, capsules);
    const eq = getQueryParameters({
      locationIds,
      locationTags,
      eventFilterState,
      searchString,
      filters,
      query,
      includeTypes: false,
      searchOperator,
    });

    // Set event types
    if (!transaction || !motion || !bookmark) {
      // Ignore event type set if all types are true

      if (transaction) {
        eq.should('type', EVENT_TYPE_TRANSACTION);
      }

      if (motion) {
        eq.should('type', EVENT_TYPE_MOTION);
      }

      if (bookmark) {
        eq.should('type', EVENT_TYPE_BOOKMARK);
      }
    }

    const data = {
      reportTemplateId,
      name,
      description,
      query: {
        query: eq.toFilter(),
      },
    };

    //
    // Save the feature in the reportMetadata
    //
    if (feature) {
      data.reportMetadata = {
        categories: [feature],
      };
    }

    if (mergeWithSubtypes) {
      data.reportMetadata = {
        ...(data.reportMetadata || {}),
        mergeWithSubtypes,
      };
    }

    if (inheritType === INHERIT_TYPE_ALL) {
      data.customerId = AuthService.session.tenantId;
    } else {
      data.userId = AuthService.session.userId;
    }

    let errorMessage = locale.reports.createError;

    PlatformApi()
      .createReport(data)
      .then((reportTemplate) => {
        // Create the report
        const { id: templateId } = reportTemplate;
        return Promise.all([reportTemplate, CallhomeApi().getRootReport({ templateId })]);
      })
      .then(([reportTemplate, rootReport]) => {
        // Update the report inheritance
        if (inheritType === INHERIT_TYPE_ALL) {
          const { id: reportId } = rootReport;
          return Promise.all([
            reportTemplate,
            CallhomeApi().updateReport({ reportId, inheritType }),
          ]);
        }
        return [reportTemplate, rootReport];
      })
      .then(([reportTemplate, rootReport]) => {
        errorMessage = locale.reports.createNotificationError;

        // Handle digest updates
        if (hasDaily || hasWeekly) {
          const customData = ReportUtil.generateReportSettings({ hasDaily, hasWeekly });

          if (rootReport.ownerType === 'user') {
            // Update settings
            const { id: reportId } = rootReport;
            return Promise.all([
              reportTemplate,
              CallhomeApi().updateReport({ reportId, ...customData }),
            ]);
          }
          // Create user custom settings entry
          const { id: reportTemplateId } = reportTemplate;
          return Promise.all([
            reportTemplate,
            rootReport,
            // We're ignoring this in the rest of the promise chain
            CallhomeApi().createReport({
              reportTemplateId,
              userId: AuthService.session.userId,
              ...customData,
            }),
          ]);
        }
        return [reportTemplate, rootReport];
      })
      .then(([reportTemplate, rootReport]) => {
        // Create the real-time notifications
        if (hasPush || hasSms || hasEmail) {
          const { id: reportTemplateId } = reportTemplate;
          return Promise.all([
            reportTemplate,
            rootReport,
            // We're ignoring this in the rest of the promise chain
            CallhomeApi().createUserNotificationForCreateReport({
              reportTemplateId,
              userId: AuthService.session.userId,
              hasEmail,
              hasSms,
              hasPush,
            }),
          ]);
        }
        return [reportTemplate, rootReport];
      })
      .then(([reportTemplate, rootReport]) => {
        // Store the mobile number (if provided)
        errorMessage = locale.error.mobileNumberStoreError;

        if (smsNumber) {
          return Promise.all([
            reportTemplate,
            rootReport,
            CallhomeApi().createSmsBinding({ smsNumber }),
          ]);
        }
        return [reportTemplate, rootReport];
      })
      .then(([reportTemplate, rootReport, smsBinding]) => {
        const helper = ReportUtil.parseNewReport({ reportTemplate, rootReport });
        toast.success(locale.reports.createSuccess, {
          autoClose: FAST_NOTIFICATION_DURATION,
        });
        AppDispatcher.dispatch(CREATE_REPORT_SUCCESS, {
          smsBinding,
          report: helper,
        });
      })
      .catch((error) => {
        toast.error(errorMessage, {
          autoClose: FAST_NOTIFICATION_DURATION,
        });
        AppDispatcher.dispatch(CREATE_REPORT_ERROR, {
          error,
        });
      });
  },

  cloudPollStart(event, accessibleAddresses, siteCamerasById, trackerName, durationChange = false) {
    setTimeout(() => {
      AppDispatcher.dispatch(CLOUD_POLL_START, {
        event,
        accessibleAddresses,
        siteCamerasById,
        trackerName,
        durationChange,
      });
    }, DISPATCH_TIMEOUT_MS);
  },

  cloudPollStop(event, siteCamerasById, trackerName) {
    setTimeout(() => {
      AppDispatcher.dispatch(CLOUD_POLL_STOP, {
        event,
        siteCamerasById,
        trackerName,
      });
    }, DISPATCH_TIMEOUT_MS);
  },

  connectPollStart(event, accessibleAddresses, siteCamerasById) {
    setTimeout(() => {
      AppDispatcher.dispatch(CONNECT_POLL_START, { event, accessibleAddresses, siteCamerasById });
    }, DISPATCH_TIMEOUT_MS);
  },

  connectPollStop(event, siteCamerasById) {
    setTimeout(() => {
      AppDispatcher.dispatch(CONNECT_POLL_STOP, {
        event,
        siteCamerasById,
      });
    }, DISPATCH_TIMEOUT_MS);
  },

  _dataUriToBlob(dataUri) {
    const binary = atob(dataUri.split(',')[1]);
    const array = [];
    for (let i = 0; i < binary.length; i++) {
      array.push(binary.charCodeAt(i));
    }

    return new Blob([new Uint8Array(array)], {
      type: 'image/jpeg',
    });
  },

  _createEvent(event, switchToEvent, cb) {
    PlatformApi()
      .createEvent(event)
      .then((newEvent) => {
        if (cb) {
          cb(null, newEvent);
        }
        AppDispatcher.dispatch(CREATE_EVENT, {
          event: newEvent,
          switchToEvent,
        });
      })
      .catch((error) => {
        console.error('error while creating new event', error);
        if (cb) {
          cb(error, null);
        }
      });
  },
};
